import React, { FC, useContext, useEffect, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import { Button, PasswordInput, Select } from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { ENDS } from 'cashbox';
import { LocalizationContext } from 'localization';
import notificationService from 'services/notificationService';
import {
  ECloudPaymentsActions,
  ESettingsAliases,
  ISetCloudPaymentsRequest,
  ISetCloudPaymentsResponse,
  SET_CLOUD_PAYMENTS,
  SettingsContext,
} from 'settings';

import s from './CloudPayments.module.scss';
import { taxOption } from './constants';
import { ICloudPaymentsProps, IFormCloudPaymentsValues } from './interfaces';

export const CloudPayments: FC<ICloudPaymentsProps> = ({
  settings,
  onClose,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    actions: { setSetting },
  } = useContext(SettingsContext);

  const variant = useMemo<'primary' | 'secondary'>(
    () => (settings.publicId ? 'secondary' : 'primary'),
    [settings]
  );

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = useForm<IFormCloudPaymentsValues>({
    mode: 'onChange',
    defaultValues: {
      publicId: '',
      secretKey: '',
      vat: null,
    },
  });

  const [setCloudPayments, { loading }] = useMutation<
    ISetCloudPaymentsResponse,
    ISetCloudPaymentsRequest
  >(SET_CLOUD_PAYMENTS);

  useEffect(() => {
    if (!settings) return;

    setValue('vat', settings.cloudPaymentsVat);
  }, [settings]);

  return (
    <form
      className={s.form}
      onSubmit={handleSubmit(({ publicId, secretKey, vat }) => {
        setCloudPayments({
          variables: {
            input: {
              action: ECloudPaymentsActions.SET,
              publicId,
              secretKey,
              vat: vat || ENDS.NDS_0,
            },
          },
        })
          .then(() => {
            notificationService.info({
              t,
              type: 'success',
              title: t('setting.updated.all'),
              message: t('setting.updated.cloudPayments'),
            });
            setSetting({
              alias: ESettingsAliases.CLOUD_PAYMENTS,
              value: '1',
            });
            onClose();
          })
          .catch(() =>
            notificationService.info({
              t,
              type: 'error',
              title: t('errors.error'),
              message: t('settings.integration.cloudPaymentsModal.error'),
            })
          );
      })}
    >
      <Controller
        render={({ field, fieldState: { error } }) => (
          <PasswordInput
            {...field}
            error={error?.message}
            title="Public key"
            autoComplete="off"
            variant={variant}
          />
        )}
        name="publicId"
        rules={{
          required: t('errors.required'),
        }}
        control={control}
      />
      <Controller
        render={({ field, fieldState: { error } }) => (
          <PasswordInput
            {...field}
            error={error?.message}
            title="Secret key"
            autoComplete="off"
            variant={variant}
          />
        )}
        name="secretKey"
        rules={{
          required: t('errors.required'),
        }}
        control={control}
      />
      <Controller
        render={({ field, fieldState: { error } }) => (
          <Select
            options={taxOption}
            {...field}
            error={error?.message}
            title="Тип налога вашей организации"
          />
        )}
        name="vat"
        rules={{
          required: t('errors.required'),
        }}
        control={control}
      />
      <div className={s.buttons_block}>
        <Button
          type="submit"
          className={s.button}
          disabled={!isDirty || loading}
        >
          {t('save')}
        </Button>
        <Button
          className={s.button}
          disabled={!settings?.cloudPayments}
          onClick={() => {
            setSetting({
              alias: ESettingsAliases.CLOUD_PAYMENTS,
              value: '0',
            });
            onClose();
          }}
        >
          {t('integration.offline')}
        </Button>
      </div>
    </form>
  );
};
